import type { ReactNode } from "react";
import { ClipboardCheck, Code, FileText, MessageSquare } from "lucide-react";
import { EmptyState } from "@/components/ui";
import { Markdown } from "@/components/code/CodeBlock";
import { scoreColor } from "@/lib/brand";
import { getScoreCategory } from "./helpers";
import { SqlPanel } from "./SqlPanel";
import type { Evaluation } from "./types";

function Section({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <span className="text-muted-foreground">{icon}</span>
        {title}
      </h3>
      {children}
    </section>
  );
}

export function EvaluationCard({ evaluation }: { evaluation: Evaluation }) {
  const score = typeof evaluation.score === "number" ? evaluation.score : undefined;
  return (
    <div className="space-y-4 rounded-md border border-border bg-card p-4">
      <div className="flex flex-wrap items-center gap-3">
        {score !== undefined ? (
          <span
            className="inline-flex items-baseline gap-1 rounded-full px-3 py-1 text-sm font-semibold text-white tabular-nums"
            style={{ backgroundColor: scoreColor(score) }}
          >
            {score.toFixed(1)}
            <span className="text-xs opacity-80">/5</span>
            <span className="ml-1 font-medium">· {getScoreCategory(score)}</span>
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">Geen score</span>
        )}
        {evaluation.confidence && <span className="text-xs text-muted-foreground">Zekerheid: {evaluation.confidence}</span>}
      </div>

      {evaluation.critique && (
        <div className="space-y-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Kritiek</div>
          <div className="border-l-2 border-sl-yellow pl-3 text-sm leading-relaxed whitespace-pre-wrap text-foreground">{evaluation.critique}</div>
        </div>
      )}

      {evaluation.reasoning && (
        <div className="space-y-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Onderbouwing</div>
          <div className="border-l-2 border-sl-grey-300 pl-3 text-sm leading-relaxed whitespace-pre-wrap text-foreground">{evaluation.reasoning}</div>
        </div>
      )}
    </div>
  );
}

/** "Resultaat" tab: answer first, then SQL, then the judge's evaluation. */
export function ResultsPanel({
  cleanAnswer,
  finalSql,
  finalEvaluation,
}: {
  cleanAnswer?: string;
  finalSql?: string;
  finalEvaluation: Evaluation | null;
}) {
  if (!finalSql && !cleanAnswer && !finalEvaluation) {
    return <EmptyState icon={<FileText className="h-5 w-5" />} title="Nog geen resultaten" description="Het antwoord en de SQL verschijnen hier zodra de vraag klaar is." />;
  }

  return (
    <div className="space-y-6">
      {/* Answer FIRST - this is what the user cares about */}
      {cleanAnswer && (
        <Section icon={<MessageSquare className="h-4 w-4" />} title="Antwoord">
          <div className="rounded-md border border-border border-l-4 border-l-success bg-card px-4 py-3">
            <Markdown>{cleanAnswer}</Markdown>
          </div>
        </Section>
      )}

      {finalSql && (
        <Section icon={<Code className="h-4 w-4" />} title="SQL-query">
          <SqlPanel sql={finalSql} title="SQL" maxHeight={480} />
        </Section>
      )}

      {finalEvaluation && (
        <Section icon={<ClipboardCheck className="h-4 w-4" />} title="Beoordeling">
          <EvaluationCard evaluation={finalEvaluation} />
        </Section>
      )}
    </div>
  );
}
